import React, { useState } from 'react';
import { Typography, Row, Col, Card, Button, Divider, Space, Steps, theme } from 'antd';
import {
  DownloadOutlined,
  SearchOutlined,
  SafetyCertificateOutlined,
  EyeOutlined,
  BranchesOutlined,
  CompassOutlined,
  ApiOutlined,
  ToolOutlined,
  TeamOutlined,
  RocketOutlined,
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const features = [
  {
    icon: <DownloadOutlined style={{ fontSize: 22, color: '#4573D2' }} />,
    title: 'Bulk Download',
    description: 'Download all or selected attachments from any Asana task in one click. Multiple files are packaged into a single zip file.',
  },
  {
    icon: <SearchOutlined style={{ fontSize: 22, color: '#4573D2' }} />,
    title: 'Search & Filter',
    description: 'Quickly find the files you need. Search by name, sort by size or date, and filter across large sets of attachments.',
  },
  {
    icon: <SafetyCertificateOutlined style={{ fontSize: 22, color: '#4573D2' }} />,
    title: 'Secure & Direct',
    description: 'Files are downloaded directly from Asana storage to your device. No intermediate servers, no copies stored elsewhere.',
  },
  {
    icon: <EyeOutlined style={{ fontSize: 22, color: '#4573D2' }} />,
    title: 'Image Preview',
    description: 'Preview images and identify file types at a glance with built-in thumbnails and file type icons before downloading.',
  },
  {
    icon: <BranchesOutlined style={{ fontSize: 22, color: '#4573D2' }} />,
    title: 'Subtask Attachments',
    description: 'Include attachments from subtasks alongside the parent task. Subtask files are clearly highlighted in the table.',
  },
  {
    icon: <CompassOutlined style={{ fontSize: 22, color: '#4573D2' }} />,
    title: 'Quick Navigation',
    description: 'Jump between tasks and projects without leaving Attachment Manager. Searchable dropdowns make navigation fast.',
  },
];

const services = [
  {
    icon: <TeamOutlined style={{ fontSize: 22, color: '#4573D2' }} />,
    title: 'Asana Consultancy',
    description: 'Workspace architecture, rollout planning, workflow design, and change management to help your team get the most from Asana.',
  },
  {
    icon: <ToolOutlined style={{ fontSize: 22, color: '#4573D2' }} />,
    title: 'Technical Configuration',
    description: 'Custom fields, rules, forms, portfolios, and advanced reporting set up by certified Asana experts.',
  },
  {
    icon: <ApiOutlined style={{ fontSize: 22, color: '#4573D2' }} />,
    title: 'Integration & Custom Apps',
    description: 'Connect Asana to Slack, Salesforce, Jira, Google Workspace, Microsoft 365, and more. Custom app development for your specific needs.',
  },
];

const setupSteps = [
  {
    title: 'Open Asana Project',
    description: 'Navigate to a project and click the Customise button in the top right corner.',
    image: '00.png',
  },
  {
    title: 'Find the App',
    description: 'In the Customise menu, select Apps and search for Attachment Manager.',
    image: '0.png',
  },
  {
    title: 'Add to Project',
    description: 'Click Add to Project, accept the EULA, and authorise the app.',
    image: '1.png',
  },
  {
    title: 'Add Widget to Task',
    description: 'Open any task in your project and click Add Downloader to add the widget.',
    image: '2.png',
  },
  {
    title: 'Open Attachment Manager',
    description: 'Click on the widget to open Attachment Manager and start downloading files.',
    image: '3.png',
  },
];

const About = () => {
  const { token } = theme.useToken();
  const [currentStep, setCurrentStep] = useState(0);

  return (
    <Row justify="center">
      <Col span={20}>
        {/* Hero */}
        <div style={{
          padding: '32px 40px',
          background: `linear-gradient(135deg, ${token.colorPrimaryBg || '#f0f5ff'} 0%, ${token.colorBgLayout} 100%)`,
          borderRadius: token.borderRadiusLG,
          marginTop: 16,
          marginBottom: 24,
        }}>
          <Title level={3} style={{ marginBottom: 8 }}>Attachment Manager for Asana</Title>
          <Paragraph style={{ fontSize: 15, maxWidth: 720, marginBottom: 0, color: token.colorTextSecondary }}>
            A free tool for downloading and organising file attachments from Asana tasks.
            Whether your team manages marketing assets, design files, contracts, or project documents,
            Attachment Manager gives you fast, secure access to every file — individually or in bulk.
          </Paragraph>
        </div>

        {/* Getting Started */}
        <Title level={4} style={{ marginBottom: 16 }}>Getting Started</Title>
        <Card
          style={{ marginBottom: 24, borderColor: token.colorBorderSecondary }}
          styles={{ body: { padding: '24px 24px 16px 24px' } }}
        >
          <Steps
            current={currentStep}
            onChange={setCurrentStep}
            size="small"
            items={setupSteps.map((step) => ({
              title: step.title,
            }))}
            style={{ marginBottom: 24 }}
          />
          <div style={{
            backgroundColor: token.colorFillAlter,
            borderRadius: token.borderRadiusLG,
            border: `1px solid ${token.colorBorderSecondary}`,
            padding: 24,
            textAlign: 'center',
          }}>
            <Paragraph strong style={{ fontSize: 14, marginBottom: 4 }}>
              Step {currentStep + 1}: {setupSteps[currentStep].title}
            </Paragraph>
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
              {setupSteps[currentStep].description}
            </Paragraph>
            <img
              src={setupSteps[currentStep].image}
              alt={setupSteps[currentStep].title}
              style={{
                maxWidth: '100%',
                maxHeight: 420,
                borderRadius: 8,
                border: `1px solid ${token.colorBorderSecondary}`,
              }}
            />
          </div>
        </Card>

        {/* Features */}
        <Title level={4} style={{ marginBottom: 16 }}>Key Features</Title>
        <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
          {features.map((feature, index) => (
            <Col xs={24} md={8} key={index}>
              <Card
                hoverable
                style={{ height: '100%', borderColor: token.colorBorderSecondary }}
                styles={{ body: { padding: 20 } }}
              >
                <Space direction="vertical" size="small">
                  {feature.icon}
                  <Text strong style={{ fontSize: 14 }}>{feature.title}</Text>
                  <Text type="secondary" style={{ fontSize: 13 }}>{feature.description}</Text>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>

        <Divider />

        {/* BDQ Services */}
        <div style={{
          padding: '24px 32px',
          backgroundColor: token.colorFillAlter,
          borderRadius: token.borderRadiusLG,
          border: `1px solid ${token.colorBorderSecondary}`,
          marginBottom: 24,
        }}>
          <Title level={4} style={{ marginBottom: 4 }}>Need help with Asana?</Title>
          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            <strong>BDQ</strong> is a trusted Asana Solutions Partner, helping organisations in the UK, US, and worldwide.
            Remote-first and flexible, BDQ provides expert services to help you get the most from Asana.
          </Paragraph>

          <Row gutter={[16, 16]}>
            {services.map((service, index) => (
              <Col xs={24} md={8} key={index}>
                <Space direction="vertical" size="small">
                  {service.icon}
                  <Text strong>{service.title}</Text>
                  <Text type="secondary" style={{ fontSize: 13 }}>{service.description}</Text>
                </Space>
              </Col>
            ))}
          </Row>

          <Divider style={{ margin: '20px 0' }} />

          <Row justify="center">
            <Button
              type="primary"
              size="large"
              icon={<RocketOutlined />}
              href="https://www.bdq.cloud/contact"
              target="_blank"
              rel="noopener noreferrer"
            >
              Get Support
            </Button>
          </Row>
        </div>
      </Col>
    </Row>
  );
};

export default About;
